import AnimateHeight from 'react-animate-height';
import styled, { css } from 'styled-components';
import { fadeIn } from '../../../helpers/animations';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing, { spacingWithBorder } from '../../../helpers/spacing';
import { Theme } from '../../../theme';
import Icon from '../../Icon';
import RichText from '../../RichText';
import {
  borderPx,
  coloredBorder,
  defaultBorder,
  getPanelColor,
  insetShadowOrSolidBackground,
  outsetShadow,
  panelContainer,
} from '../styles';
import { BasePanelProps, PanelColors } from '../types';

export type PanelStyleProps = BasePanelProps & {
  cornerLabel?: {
    iconOnly: boolean;
    isDisplayed: boolean;
  };
};

const cornerLabelAbsolutePositioning = (
  colors: PanelColors | undefined,
  theme: Theme
): string => `
      border: ${borderPx}px solid ${getPanelColor(theme, colors?.primaryColor)};
      position: absolute;
      top: -${borderPx}px;
      right: -${borderPx}px;
      left: auto;
      border-radius: 0 ${theme.cardRadius};
     `;

export const Container = styled.div<BasePanelProps>`
  ${panelContainer}
  ${({ borderShadows }) => borderShadows?.displayOutset && outsetShadow}
  ${({ colors, borderShadows }) =>
    !borderShadows?.displayOutset && !colors?.primaryColor && defaultBorder}
`;

export const StyledPanel = styled.div<BasePanelProps>`
  position: relative;
  ${insetShadowOrSolidBackground}
  ${coloredBorder}
  border-radius: ${({ theme }) => theme.cardRadius};

  z-index: 2;
`;

export const PanelBodySpacing = styled.div<PanelStyleProps>`
  /* If cornerLabel contains only an icon: it is out of flow (positioned in the top right using 'position: absolute')  
  and so needs padding on mobile when an icon is shown without text */
  padding-top: ${({ cornerLabel }) =>
    cornerLabel?.iconOnly && cornerLabel?.isDisplayed
      ? spacingWithBorder(7, borderPx)
      : ''};

  ${mediaQuery.tabletPortrait`
    padding-top: 0
  `};

  transition: padding-top 0.3s ease-out;
`;

export const CornerLabel = styled.div<PanelStyleProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & > *:not(:last-child) {
    margin-right: ${spacing(2)};
  }
  padding: ${spacingWithBorder(1.5, borderPx)} ${spacingWithBorder(2, borderPx)};

  ${({ colors, cornerLabel, theme }) => {
    const panelColor = getPanelColor(theme, colors?.primaryColor);

    return css`
      ${RichText} {
        &,
        b,
        strong {
          color: ${panelColor};
        }
      }
      border-bottom: ${borderPx}px solid ${panelColor};
      border-radius: ${theme.cardRadius} ${theme.cardRadius} 0 0;
      background: ${getPanelColor(theme, colors?.lightColor)};

      /* cornerLabel is out of flow ('position: absolute') when there is only a icon (all devices) and on tabletPortrait (and larger) devices */
      ${cornerLabel?.iconOnly
        ? css`
          ${cornerLabelAbsolutePositioning(colors, theme)}
          padding: ${spacingWithBorder(2, borderPx)};
        `
        : css`
            ${mediaQuery.tabletPortrait`
              ${cornerLabelAbsolutePositioning(colors, theme)}
            `};
          `}
    `;
  }}

  animation: ${fadeIn} 0.3s ease-out;
`;

export const CornerLabelRichText = styled(RichText)`
  ${fonts.paragraphSmall};
  b,
  strong {
    ${fonts.paragraphLarge};

    & {
      font-weight: bold;
    }
  }

  ${mediaQuery.tabletPortrait`
    ${fonts.paragraph};
  `}
`;

export const CornerLabelIcon = styled(Icon)`
  min-width: ${spacing(3)};
`;

export const AnimateHeightWithZIndex = styled(AnimateHeight)`
  z-index: 1;
`;

export const BottomLabel = styled.div<PanelStyleProps>`
  position: relative;

  margin-top: ${({ theme }) => `calc(-2 * ${theme.cardRadius})`};
  padding-top: ${({ theme }) => `calc(2 * ${theme.cardRadius})`};

  ${({ colors, theme }) => {
    const panelColor = getPanelColor(theme, colors?.primaryColor);

    return css`
      border: ${borderPx}px solid ${panelColor};
      border-radius: ${theme.cardRadius};
      background: ${getPanelColor(theme, colors?.lightColor)};
    `;
  }};

  animation: ${fadeIn} 0.3s ease-out;
`;

export const BottomLabelRichText = styled(RichText)`
  ${fonts.paragraphSmall};
  margin: ${spacing(1.5)} ${spacing(2)};

  ${mediaQuery.tabletLandscape`
    margin: ${spacing(2)} ${spacing(4)};
  `}
`;
